import {
    STRAIGHT_OUT_MIN_DARTS,
    DOUBLE_OUT_MIN_DARTS,
    MASTER_OUT_MIN_DARTS,
} from '@/constants/checkoutTable'

// Every distinct score a single dart can produce (miss, singles, doubles,
// triples, outer bull, bull), sorted ascending so the search can stop early.
const ACHIEVABLE_DART_VALUES = (() => {
    const values = new Set([0])
    for (let n = 1; n <= 20; n++) {
        values.add(n)
        values.add(2 * n)
        values.add(3 * n)
    }
    values.add(25)
    values.add(50)
    return Array.from(values).sort((a, b) => a - b)
})()

// Whether a dart that brings the remaining to exactly 0 is a *legal* finisher
// for the given checkout type. Double-out needs a double (bull = double 25),
// master-out also allows triples, straight-out allows anything that scores.
function canValueFinish(value, checkOutType) {
    if (value <= 0) {
        return false
    }
    const isDouble = (value >= 2 && value <= 40 && value % 2 === 0) || value === 50
    if (checkOutType === 'double') {
        return isDouble
    }
    if (checkOutType === 'master') {
        const isTriple = value >= 3 && value <= 60 && value % 3 === 0
        return isDouble || isTriple
    }
    return true
}

class DoubleAttemptsCalculator {
    getMinDartsTable(checkOutType) {
        switch (checkOutType) {
            case 'single':
                return STRAIGHT_OUT_MIN_DARTS
            case 'master':
                return MASTER_OUT_MIN_DARTS
            case 'double':
            default:
                return DOUBLE_OUT_MIN_DARTS
        }
    }

    isOneDartFinish(remaining, table) {
        // table[remaining] === 1 already encodes finishability per checkout type
        // (e.g. straight-out can finish from 1, double-out cannot).
        return remaining >= 1 && remaining < table.length && table[remaining] === 1
    }

    /**
     * Returns every number of "darts thrown at a double" that is consistent with
     * a turn that started on `remainingBefore` and scored `scoreThrown`, by
     * enumerating all valid dart paths. A *scoring* dart counts as a double
     * attempt when the score *before* throwing it is a one-dart finish (e.g. 40,
     * 32, 50 for double-out). A *miss* from such a position is ambiguous — it may
     * have been a missed double attempt or a deliberate set-up — so it counts as
     * either 0 or 1. The result is the deduplicated, sorted set of possibilities:
     *   - length 1  -> the count is certain (no need to ask the player)
     *   - length >1 -> genuinely ambiguous from the total alone
     */
    computePossibleAttempts(remainingBefore, scoreThrown, checkOutType = 'double') {
        const table = this.getMinDartsTable(checkOutType)
        const remaining = remainingBefore
        const score = scoreThrown

        if (!Number.isInteger(remaining) || !Number.isInteger(score) || remaining < 0 || score < 0) {
            return []
        }
        
        // If the starting score can't be finished at all, no dart was at a double.
        if (!table[remaining]) {
            return [0]
        }

        const after = remaining - score
        // Leaving exactly 1 only busts when the checkout needs a finishing dart
        // you can't throw from 1 (double-out, master-out). Straight-out can
        // finish from 1, so leaving 1 there is a normal, non-busting turn.
        const oneLeftBusts = checkOutType !== 'single'
        const isFinish = after === 0
        const isBust = after < 0 || (after === 1 && oneLeftBusts)
        const results = new Set()

        const visit = (current, dartsThrown, scoreSoFar, attempts) => {
            if (dartsThrown >= 3) {
                if (!isFinish && !isBust && scoreSoFar === score) {
                    results.add(attempts)
                }
                return
            }

            const onFinish = this.isOneDartFinish(current, table)

            for (const value of ACHIEVABLE_DART_VALUES) {
                const newScore = scoreSoFar + value
                if (newScore > score) {
                    break
                }

                const newRemaining = current - value

                // A dart thrown while on a one-dart finish counts as a double
                // attempt. A miss in that position is ambiguous though — the
                // player may have thrown at the double and missed, or set up a
                // better number — so both 0 and 1 are possible. A scoring dart
                // off a finish never counts.
                const isMiss = value === 0
                const attemptIncrements = onFinish ? (isMiss ? [0, 1] : [1]) : [0]

                if (newRemaining === 0) {
                    if (newScore === score) {
                        for (const inc of attemptIncrements) {
                            const newAttempts = attempts + inc
                            if (canValueFinish(value, checkOutType)) {
                                if (isFinish) {
                                    results.add(newAttempts)
                                }
                            } else if (isBust) {
                                results.add(newAttempts)
                            }
                        }
                    }
                    continue
                }

                if (newRemaining < 0 || (newRemaining === 1 && oneLeftBusts)) {
                    if (isBust && newScore === score) {
                        for (const inc of attemptIncrements) {
                            results.add(attempts + inc)
                        }
                    }
                    continue
                }

                // newRemaining >= 2 (or === 1 in straight-out): the turn continues.
                for (const inc of attemptIncrements) {
                    visit(newRemaining, dartsThrown + 1, newScore, attempts + inc)
                }
            }
        }

        visit(remaining, 0, 0, 0)

        return Array.from(results).sort((a, b) => a - b)
    }

    /**
     * Dart-by-dart variant for the per-dart grid, where the exact darts are
     * known. The only thing the darts don't reveal is intent: which darts thrown
     * from a finishing position were actually aimed at the checkout double.
     * Returns the plausible range of "darts at a double" as a sorted, contiguous
     * list:
     *   - min = darts that actually finished the leg (0 or 1 — a checkout is a
     *           certain attempt)
     *   - max = darts thrown while on a one-dart finish (every dart that *could*
     *           have been an attempt)
     *
     * `darts` is the thrown sequence, each `{ value, isDouble, isTriple }` where
     * `value` is the dart's score. Enumeration stops at the dart that checks out
     * or busts, mirroring how a turn actually ends.
     */
    computePossibleAttemptsFromDarts(remainingBefore, darts, checkOutType = 'double') {
        const table = this.getMinDartsTable(checkOutType)

        if (!Number.isInteger(remainingBefore) || remainingBefore < 0 || !Array.isArray(darts)) {
            return [0]
        }

        const oneLeftBusts = checkOutType !== 'single'
        let remaining = remainingBefore
        let onFinishCount = 0
        let checkoutCount = 0

        for (const dart of darts) {
            const value = Number.isInteger(dart?.value) ? dart.value : 0
            const after = remaining - value

            if (this.isOneDartFinish(remaining, table)) {
                onFinishCount += 1

                const finishesLeg =
                    after === 0 &&
                    (checkOutType === 'single'
                        ? value > 0
                        : checkOutType === 'master'
                          ? dart?.isDouble || dart?.isTriple
                          : dart?.isDouble)

                if (finishesLeg) {
                    checkoutCount += 1
                }
            }

            remaining = after

            // The turn ends as soon as a dart checks out or busts.
            if (remaining === 0) break
            if (remaining < 0 || (remaining === 1 && oneLeftBusts)) break
        }

        const min = checkoutCount
        const max = Math.max(onFinishCount, checkoutCount)
        const options = []
        for (let i = min; i <= max; i++) {
            options.push(i)
        }

        return options.length > 0 ? options : [0]
    }

    /**
     * Resolves the double-attempt count for a turn. When the answer is certain
     * `value` holds it (and the modal can be skipped); otherwise `value` is null
     * and `options` lists exactly the counts the player should choose between.
     */
    resolveAttempts(remainingBefore, scoreThrown, checkOutType = 'double') {
        const options = this.computePossibleAttempts(remainingBefore, scoreThrown, checkOutType)
        return {
            value: options.length === 1 ? options[0] : null,
            options,
        }
    }
}

export default new DoubleAttemptsCalculator()
